import { useMutation, useQuery, useQueryClient } from '@zg-rentals/react-query5';
import { useFeatureFlag } from '@/src/lib/feature-flags';
import {
  hpTours,
  type ScheduledTour,
  type TourMutationResponse,
} from '@/src/lib/api/endpoints/hpTours';
import {
  createMockCancelTourResponse,
  createMockRequestTourResponse,
  createMockRescheduleTourResponse,
  type RequestTourMutationBody,
  type RescheduleTourMutationBody,
} from './mockHpTours';
import { hpToursKeys } from './queryKeys';

type CancelTourResponse = Awaited<ReturnType<typeof hpTours.cancelTour>>;

function upsertScheduledTour(
  tours: Array<ScheduledTour> | undefined,
  tour: ScheduledTour,
): Array<ScheduledTour> {
  const existing = tours ?? [];
  const withoutTour = existing.filter(
    (item) => item.tourId !== tour.tourId && item.listingAlias !== tour.listingAlias,
  );
  return [...withoutTour, tour];
}

function updateScheduledTourCache(
  queryClient: ReturnType<typeof useQueryClient>,
  response: TourMutationResponse,
) {
  if (!response.success || !response.data?.tourId) return;

  queryClient.setQueryData<Array<ScheduledTour>>(
    hpToursKeys.scheduledToursForUser,
    (current) => upsertScheduledTour(current, response.data as ScheduledTour),
  );
}

export function useScheduledToursForUser(enabled = true) {
  return useQuery({
    queryKey: hpToursKeys.scheduledToursForUser,
    queryFn: hpTours.scheduledToursForUser,
    enabled,
    staleTime: 0,
  });
}

export function useRequestTour() {
  const queryClient = useQueryClient();
  const mockBookTourEnabled = useFeatureFlag('mock-book-tour');

  return useMutation({
    mutationFn: (body: RequestTourMutationBody) => {
      if (mockBookTourEnabled) {
        return Promise.resolve(createMockRequestTourResponse(body));
      }

      const apiBody = { ...body };
      delete apiBody.zoneId;
      return hpTours.requestTour(apiBody);
    },
    onSuccess: (response) => updateScheduledTourCache(queryClient, response),
  });
}

export function useRescheduleTour() {
  const queryClient = useQueryClient();
  const mockBookTourEnabled = useFeatureFlag('mock-book-tour');

  return useMutation({
    mutationFn: (body: RescheduleTourMutationBody) => {
      if (mockBookTourEnabled) {
        return Promise.resolve(createMockRescheduleTourResponse(body));
      }

      const apiBody = { ...body };
      delete apiBody.zoneId;
      return hpTours.rescheduleTour(apiBody);
    },
    onSuccess: (response) => updateScheduledTourCache(queryClient, response),
  });
}

export function useCancelTour() {
  const queryClient = useQueryClient();
  const mockBookTourEnabled = useFeatureFlag('mock-book-tour');

  return useMutation({
    mutationFn: (tourId: string): Promise<CancelTourResponse> => {
      if (mockBookTourEnabled) {
        return Promise.resolve(createMockCancelTourResponse());
      }

      return hpTours.cancelTour(tourId);
    },
    onSuccess: (response, tourId) => {
      if (!response.success) return;
      queryClient.setQueryData<Array<ScheduledTour>>(
        hpToursKeys.scheduledToursForUser,
        (current) => (current ?? []).filter((tour) => tour.tourId !== tourId),
      );
    },
  });
}
